import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { ItilConsolidatedSection, ItilTranslationStatus } from '../types';
import { itilColors, itilConsolidatedPanelSx, itilTranslateBtnSx } from '../styles';
import ItilStatusBadge from './ItilStatusBadge';
import ItilTranslateActions from './ItilTranslateActions';
import Button from '@mui/material/Button';

type Props = {
  status: ItilTranslationStatus;
  sections: ItilConsolidatedSection[];
  errorMessage?: string | null;
  onTranslate: () => void;
  onContinueWithoutItil?: () => void;
  itilConfigured?: boolean;
};

function ItilConsolidatedPanel({
  status,
  sections,
  errorMessage,
  onTranslate,
  onContinueWithoutItil,
  itilConfigured = true
}: Props) {
  const { t } = useTranslation('global');

  const renderBody = () => {
    if (!itilConfigured) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#6f93a8',
            p: 4,
            minHeight: 240
          }}
        >
          <Typography sx={{ fontSize: '12.5px', fontStyle: 'italic', maxWidth: 280 }}>
            {t('itilGenius.consolidated.noClosingFieldsConfigured')}
          </Typography>
        </Box>
      );
    }

    if (status === 'idle') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#6f93a8',
            p: 4,
            minHeight: 240,
            gap: 1
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#d9ebf5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20
            }}
          >
            ↻
          </Box>
          <Typography sx={{ fontSize: '12.5px', fontStyle: 'italic', maxWidth: 240 }}>
            {t('itilGenius.consolidated.idle')}
          </Typography>
        </Box>
      );
    }

    if (status === 'loading') {
      return (
        <Box sx={{ p: 2 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton height={9} sx={{ width: '38%', mb: 1 }} variant="rounded" />
              <Skeleton height={12} sx={{ width: '95%', mb: 0.5 }} variant="rounded" />
              <Skeleton height={12} sx={{ width: '85%' }} variant="rounded" />
            </Box>
          ))}
        </Box>
      );
    }

    if (status === 'error') {
      return (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              bgcolor: '#fdeceb',
              border: '1px solid #f3c9c6',
              borderRadius: '7px',
              p: 1.5
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#b3261e', mb: 1 }}>
              ! {t('itilGenius.messages.serviceUnavailableTitle')}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: '#7c3a36', mb: 1.5 }}>
              {errorMessage || t('itilGenius.consolidated.errorBody')}
            </Typography>
            {onContinueWithoutItil && (
              <Button
                onClick={onContinueWithoutItil}
                size="small"
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '11.5px' }}
                variant="outlined"
              >
                {t('itilGenius.actions.continueWithoutItil')}
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    if (status === 'skipped') {
      return (
        <Box
          sx={{
            m: 2,
            p: 1.5,
            bgcolor: '#f4f6f9',
            border: '1px dashed #c9d4e2',
            borderRadius: '7px',
            fontSize: '12.5px',
            color: itilColors.inkSoft
          }}
        >
          {t('itilGenius.consolidated.skippedBody')}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          opacity: status === 'stale' ? 0.45 : 1,
          maxHeight: 560,
          overflow: 'auto'
        }}
      >
        {sections.map((sec) => (
          <Box key={sec.label || 'consolidated'} sx={{ mb: sec.label ? 2 : 0 }}>
            {sec.label ? (
              <Typography
                sx={{
                  fontSize: '10.5px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: '#3f7fa0',
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  '&::before': {
                    content: '""',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#5aa0c4'
                  }
                }}
              >
                {sec.label}
              </Typography>
            ) : null}
            <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: '#27333f' }}>
              {sec.text || '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const footHint = () => {
    if (status === 'ready') {
      return t('itilGenius.consolidated.readyHint');
    }
    if (status === 'stale') {
      return t('itilGenius.messages.staleHint');
    }
    if (status === 'skipped') {
      return t('itilGenius.consolidated.skippedFoot');
    }
    return null;
  };

  return (
    <Box sx={itilConsolidatedPanelSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: '12px 15px',
          borderBottom: `1px solid ${itilColors.itilBorder}`,
          bgcolor: '#e7f2f9'
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: '#2e6a8a',
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }}
          >
            {t('itilGenius.consolidated.title')}
          </Typography>
          <Typography sx={{ fontSize: '10.5px', color: '#5f8aa3' }}>
            {t('itilGenius.consolidated.subtitle')}
          </Typography>
        </Box>
        <ItilStatusBadge status={status} />
      </Box>

      {renderBody()}

      <Box
        sx={{
          p: '11px 15px',
          borderTop: `1px solid ${itilColors.itilBorder}`,
          bgcolor: '#eaf4fa',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        {footHint() && (
          <Typography
            sx={{
              fontSize: 11,
              color: status === 'stale' ? itilColors.req : status === 'ready' ? itilColors.ok : itilColors.inkSoft,
              flex: 1
            }}
          >
            {status === 'ready' && '✓ '}
            {status === 'stale' && '! '}
            {footHint()}
          </Typography>
        )}
        <Button
          disabled={!itilConfigured || status === 'loading'}
          onClick={onTranslate}
          size="small"
          sx={{ ...itilTranslateBtnSx, ml: 'auto' }}
          variant="outlined"
        >
          ↻{' '}
          {status === 'ready' || status === 'stale'
            ? t('itilGenius.actions.retranslate')
            : t('itilGenius.actions.translate')}
        </Button>
      </Box>
    </Box>
  );
}

export default ItilConsolidatedPanel;
